import { log } from 'log';
import { useCallback, useRef } from 'react';
import { useShowNotification } from 'js/hooks/useShowNotification';
import { pick, prop } from 'ramda';
import { useAppSelector, useGameConfigurationSelector } from 'js/store/hooks';
import * as server from 'js/components/common/Game/FavoriteIcon/hooks/server';
import { useFavoritesTracking } from 'js/components/common/Game/FavoriteIcon/hooks/useFavoritesTracking';
import { useSmartTranslations } from 'js/hooks/i18n';
import { useDispatch } from 'react-redux';
import { toggleFavoriteGame } from 'js/hooks/games/store/actions';

export const useToggleFavorite = ({ code, isFavorite, gameFeed, isAnimatedFromInfoPopup }) => {
    const dispatch = useDispatch();
    const { dynamicTags } = useAppSelector(pick(['dynamicTags']));
    const favoriteGames = useGameConfigurationSelector(prop('favoriteGames'));
    const showNotification = useShowNotification();
    const trackAPIEvent = useFavoritesTracking({ gameCode: code, isFavorite, gameFeed });
    const inProgress = useRef(false);
    const t = useSmartTranslations();
    const maxFavorites = dynamicTags && dynamicTags.DTAG_MAX_FAVORITES || 100;

    return useCallback(() => {
        // prevent double-click
        if (inProgress.current) {
            return;
        }

        inProgress.current = true;

        trackAPIEvent();

        (async () => {
            try {
                const newValue = !isFavorite;
                let count = favoriteGames?.length || 0;
                let isError = false;
                try {
                    await server.changeFavorite({ code, isFavorite: newValue });
                } catch (error) {
                    console.error(error?.response?.data?.errorMessage);
                    isError = true;
                    if (error?.response?.data?.errorCode === 105) {
                        showNotification(
                            'playtech.mobile.system.toast.favorites.error.max', { variant: 'error' });
                    }
                } finally {
                    if (!isError) {
                        const translation = t(`playtech.mobile.system.toast.favorites${newValue ? 'Add' : 'Remove'}`);
                        const of = t('playtech.mobile.system.toast.favorites.of');
                        dispatch(toggleFavoriteGame({ gameCodes: [code], isFavorite: newValue }));
                        newValue ? count += 1 : count -= 1;
                        !isAnimatedFromInfoPopup && showNotification(`${translation} (${count} ${of} ${maxFavorites})`, { variant: 'info' });
                    }
                }
            } catch (error) {
                log('error', error);
            } finally {
                inProgress.current = false;
            }
        })();
    }, [
        trackAPIEvent,
        isFavorite,
        favoriteGames?.length,
        code,
        showNotification,
        t,
        dispatch,
        isAnimatedFromInfoPopup,
        maxFavorites
    ]);
};
